import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";

/**
 * Dark/Light/System theme (backlog UX-11) — personal, per-browser choice,
 * not shared with teammates (an account can have up to 3 members since
 * AUTH-5; each picks independently). Mirrors src/lib/i18n's own
 * localStorage-backed provider pattern, minus the account-persistence half
 * (language is tied to the account; theme deliberately isn't).
 */

export type ThemeChoice = "dark" | "light" | "system";
export type ResolvedTheme = "dark" | "light";
export const THEME_STORAGE_KEY = "snapdraw.theme";

const ThemeContext = createContext<{
  theme: ThemeChoice;
  resolved: ResolvedTheme;
  setTheme: (next: ThemeChoice) => void;
} | null>(null);

function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readStored(): ThemeChoice {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
  return raw === "dark" || raw === "light" || raw === "system" ? raw : "system";
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.dataset["theme"] = resolved;
  root.classList.toggle("dark", resolved === "dark");
  root.classList.toggle("light", resolved === "light");
  root.style.colorScheme = resolved;
}

// index.html has an inline <script> in <head> that duplicates
// readStored()/systemTheme()/applyTheme()'s logic as a plain string — it
// has to run during HTML parsing, before any stylesheet or this bundle
// loads, to actually prevent a flash of the wrong theme; rendering it from
// a React component would run far too late to matter. Keep both in sync
// by hand if this logic ever changes.

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeChoice>("system");
  const [system, setSystem] = useState<ResolvedTheme>("dark");

  // Read after mount, not in a state initializer — the index.html bootstrap
  // script has already applied the correct theme to <html> before React
  // even runs, so this just brings React's own state in sync with it.
  useEffect(() => {
    setThemeState(readStored());
    setSystem(systemTheme());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setSystem(mq.matches ? "light" : "dark");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolved: ResolvedTheme = theme === "system" ? system : theme;
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setTheme = useCallback((next: ThemeChoice) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* private mode — theme still applies for this tab, just doesn't persist */
    }
  }, []);

  const value = useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme]);
  return (
    <ThemeContext.Provider value={value}>
      {children}
      <Toaster theme={resolved} position="bottom-center" />
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
